import { API_V2 } from '../lib/axios';
import { IUserNotification } from '../types/notification';

const NOTIFICATIONS_ENDPOINT = '/notifications';

/**
 * Get user notifications
 * @param userID - The user ID
 * @param seen - Filter by seen status (default: false for unseen notifications)
 * @returns Array of user notifications ordered by creation date
 */
const getUserNotifications = async (
  userID: string,
  seen = false,
): Promise<IUserNotification[]> => {
  const response = await API_V2.get<IUserNotification[]>(
    `${NOTIFICATIONS_ENDPOINT}/user/${userID}`,
    {
      params: { seen, r: `${Math.random()}` },
    },
  );

  // Sort by created_on date, most recent first
  return response.data.sort(
    (a, b) => new Date(b.created_on).getTime() - new Date(a.created_on).getTime(),
  );
};

/**
 * Mark a single notification as seen
 * @param notificationID - The notification ID
 * @param userID - The user ID
 * @returns The updated notification
 */
const markAsSeen = async (
  notificationID: string,
  userID: string,
): Promise<IUserNotification> => {
  const response = await API_V2.get<IUserNotification>(
    `${NOTIFICATIONS_ENDPOINT}/${notificationID}/user/${userID}/seen`,
    {
      params: { r: `${Math.random()}` },
    },
  );

  return response.data;
};

/**
 * Mark all notifications as seen for a user
 * @param userID - The user ID
 * @returns The response data
 */
const markAllAsSeen = async (userID: string): Promise<any> => {
  const response = await API_V2.get(
    `${NOTIFICATIONS_ENDPOINT}/all/user/${userID}/seen`,
    {
      params: { r: `${Math.random()}` },
    },
  );

  return response.data;
};

/**
 * Mark a single notification as deleted
 * @param notificationID - The notification ID
 * @param userID - The user ID
 * @returns The updated notification
 */
const markAsDeleted = async (
  notificationID: string,
  userID: string,
): Promise<IUserNotification> => {
  const response = await API_V2.get<IUserNotification>(
    `${NOTIFICATIONS_ENDPOINT}/${notificationID}/user/${userID}/delete`,
    {
      params: { r: `${Math.random()}` },
    },
  );

  return response.data;
};

/**
 * Mark all notifications as deleted for a user
 * @param userID - The user ID
 * @returns The response data
 */
const markAllAsDeleted = async (userID: string): Promise<any> => {
  const response = await API_V2.get(
    `${NOTIFICATIONS_ENDPOINT}/all/user/${userID}/delete`,
    {
      params: { r: `${Math.random()}` },
    },
  );

  return response.data;
};

/**
 * Get the count of unseen notifications for a user
 * @param userID - The user ID
 * @returns The count of unseen notifications
 */
const getUnseenCount = async (userID: string): Promise<number> => {
  try {
    const notifications = await getUserNotifications(userID, false);
    return notifications.length;
  } catch (error) {
    console.error('Error getting unseen notification count:', error);
    return 0;
  }
};

export const NotificationService = {
  getUserNotifications,
  markAsSeen,
  markAllAsSeen,
  markAsDeleted,
  markAllAsDeleted,
  getUnseenCount,
};
